import { select, Selection } from 'd3-selection'
import { css } from 'emotion'
import theme from 'live/services/theme'
import { LithologyPc, DashTheme } from '../types'
import { AXIS_SIZE } from 'live/services/charts/axis.styled'

const rotatedLeg = css`
    position: absolute;
    top: 0px;
    left: 0px;
    height: 100%;
    transform: rotate(-90deg);
`

export type LegendItem = {
    text: string
    color: string
    background?: string
}

export default class Legend {
    private parentEl: HTMLElement
    private tooltipEl: Selection<HTMLDivElement, null, null, undefined>
    private legendEl: Selection<HTMLUListElement, null, null, undefined>
    private depthEl: Selection<HTMLDivElement, null, null, undefined>
    private legendItems: Array<LegendItem>
    private sortLegend: (a: LegendItem, b: LegendItem) => number
    private dashTheme: DashTheme

    constructor(parentEl: HTMLElement, dashTheme: DashTheme) {
        this.parentEl = parentEl
        this.dashTheme = dashTheme
        this.legendItems = []
        this.tooltipEl = select<HTMLElement, null>(parentEl).append('div')
        this.depthEl = this.tooltipEl
            .append('div')
            .classed(this.getDepthCss(), true)
            .attr('aria-label', 'depth indicator')

        this.legendEl = this.tooltipEl
            .append('ul')
            .classed(this.getLegendCss(), true)
            .attr('aria-label', 'lithologies legends')
        this.sortLegend = (a, b) => (a.text < b.text ? -1 : a.text > b.text ? 1 : 0)
    }

    getEl(): Selection<HTMLUListElement, null, null, undefined> {
        return this.legendEl
    }

    renderItem(selection: Selection<HTMLLIElement, LegendItem, HTMLUListElement, undefined>): void {
        selection
            .selectAll('i')
            .data(d => [d])
            .join('i')
            .style('background-color', d => d.color)
            .style('background-image', d => (d.background ? `url(${d.background})` : null))

        selection
            .selectAll('span')
            .data(d => [d])
            .join('span')
            .text(d => d.text)
            .style('padding-left', '5px')
    }

    groupByLithology(liths: LithologyPc[]): LithologyPc[] {
        return Array.from(
            liths
                .reduce((grouped, lith) => {
                    const key = lith.text
                    const value = +lith.value
                    if (!grouped.has(key)) {
                        grouped.set(key, { text: key, value: value })
                    } else {
                        grouped.get(key).value += value
                    }

                    return grouped
                }, new Map())
                .values()
        )
    }

    renderHoveredValues(targetHovered: LithologyPc[] | { text: string }[], depthHovered?: string): void {
        if (targetHovered as LithologyPc[]) targetHovered = this.groupByLithology(targetHovered)
        const isHovered = (d: LegendItem): boolean => targetHovered.some(lit => lit.text === d.text)

        const hoveredItensFirst = (a: LegendItem, b: LegendItem): number => {
            const aIsHovered = isHovered(a)
            const bIsHovered = isHovered(b)
            if (aIsHovered && !bIsHovered) {
                return -1
            }

            if (bIsHovered && !aIsHovered) {
                return 1
            }

            return this.sortLegend(a, b)
        }

        this.legendItems.sort(hoveredItensFirst)

        const lis = this.legendEl
            .classed(this.hasHover(), true)
            .selectAll('li')
            .data(this.legendItems, d => d.text)
            .join(update => update)

        lis.filter(d => isHovered(d))
            .classed('high', true)
            .selectAll('span.value')
            .data(d => targetHovered.filter(t => t.text === d.text))
            .join('span')
            .classed('value', true)

        lis.filter(d => !isHovered(d))
            .classed('high', false)
            .selectAll('span.value')
            .data([''])
            .join('span')
            .classed('value', true)
            .text('')

        this.depthEl
            .style('opacity', 1)
            .selectAll('p')
            .data([depthHovered])
            .join('p')
            .text(d => d)
    }

    clearHover(): void {
        this.depthEl.style('opacity', 0)
        this.legendEl
            .classed(this.hasHover(), false)
            .selectAll('li')
            .data(this.legendItems.sort(this.sortLegend), d => d.text)
            .join(update => update)
    }

    render(
        items: Array<LegendItem>,
        vertical: boolean,
        size: number,
        currentTheme: DashTheme,
        renderDepth?: boolean
    ): void {
        this.dashTheme = currentTheme || 'light'
        this.tooltipEl
            .style('width', vertical ? `${this.parentEl?.offsetWidth}px` : `${this.parentEl?.offsetHeight}px`)
            .classed(rotatedLeg, !vertical)
        this.legendEl?.style('max-height', `${size}px`).attr('class', this.getLegendCss())
        this.depthEl.attr('class', this.getDepthCss())
        this.legendItems = items.sort(this.sortLegend).slice()
        if (renderDepth) {
            this.depthEl
                .selectAll('span')
                .data(['Depth: '])
                .join('span')
                .text(d => d)
        }
        this.legendEl
            .selectAll('li')
            .data(this.legendItems)
            .join('li')
            .style('padding', '1px 5px')
            .call(this.renderItem)
    }

    destroy(): void {
        this.legendEl.remove()
    }

    private getDepthCss = (): string => {
        return css`
            padding: 0px 8px;
            display: flex;
            justify-content: center;
            opacity: 0;
            background: transparent;
            color: ${this.dashTheme === 'light' ? theme['text-color'] : 'white'};
            span {
                font-weight: bold;
                font-size: 13px;
            }
            p {
                margin: 0px 0px 0px 2px;
                color: ${this.dashTheme === 'light' ? theme['text-color'] : 'white'};
            }
        `
    }

    private hasHover = (): string => css`
        li {
            opacity: 0.5;
        }
        li.high {
            opacity: 1;
        }
        span.value {
            opacity: 1 !important;
        }
    `

    private getLegendCss = (): string => css`
        overflow-y: auto;
        border-radius: 2px;
        &::-webkit-scrollbar {
            width: 7px;
            background: #f8f8f9;
        }
        &::-webkit-scrollbar-thumb {
            background: ${theme['gray-light']};
            border-radius: 2px;
            height: 50px;
            background-clip: padding-box;
        }
        li {
            background-color: var(--gray-200);
            height: ${AXIS_SIZE - 1}px;
            display: flex;
            align-items: center;
            white-space: nowrap;
            padding: 1px 15px;
            margin-bottom: 1px;
            i {
                background: transparent;
                display: inline-block;
                min-width: 20px;
                height: 20px;
                border-radius: 2px;
                vertical-align: text-bottom;
            }
            span {
                box-sizing: border-box;
                font-size: 11px;
                white-space: nowrap;
                line-height: 20px;
                font-family: SourceSansPro-Semibold, 'Source Sans Pro', sans-serif;
                color: ${this.dashTheme === 'light' ? '#505062' : 'white'};
                -webkit-font-smoothing: subpixel-antialiased;
                -moz-osx-font-smoothing: grayscale;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            span.value {
                opacity: 0;
                font-weight: bold;
                font-size: 11.5px;
            }
        }
    `
}
